import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { AdminBrandBannerDetailsForm } from '../../models/admin-brand-banner-details-form';
import { AdminBrandBannerDataSourceService } from '../../services/admin-brand-banner-data-source.service';
import { AdminBrandBannerDetailsFormComponent } from '../admin-brand-banner-details-form/admin-brand-banner-details-form.component';

@Component({
  selector: 'app-admin-brand-banner-edit',
  imports: [
    AdminBrandBannerDetailsFormComponent,
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
  ],
  templateUrl: './admin-brand-banner-edit.component.html',
  styleUrl: './admin-brand-banner-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminBrandBannerEditComponent {
  private readonly bannerDataSource = inject(AdminBrandBannerDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly brandId = input.required<Guid>();
  public readonly bannerId = input.required<Guid>();
  public readonly submitSuccessEvent = output<void>();

  protected readonly brandId$ = toObservable(this.brandId);
  protected readonly bannerId$ = toObservable(this.bannerId);
  protected readonly form$ = this.formSource();

  public handleSubmitClickEvent(data: AdminBrandBannerDetailsForm): void {
    this.bannerDataSource
      .updateByFormData({
        bannerId: this.bannerId(),
        brandId: this.brandId(),
        data,
      })
      .pipe(once())
      .subscribe({
        next: () => this.submitSuccessEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminBrandBannerDetailsForm> {
    return combineLatest([this.brandId$, this.bannerId$]).pipe(
      map(([brandId, bannerId]) => ({ bannerId, brandId })),
      switchMap(({ bannerId, brandId }) =>
        this.bannerDataSource.fetchDetailsFormById({
          bannerId,
          brandId,
        }),
      ),
    );
  }
}
